import {
  AfterContentChecked,
  AfterViewChecked,
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { ModalType } from '../../../shared/enums/modal-type.enums';
import { IconComponent } from '../../atoms/icon/icon.component';

import { Colors, Sizes } from '../../../shared/enums/shared.enums';
import { SafeResourceUrl } from '@angular/platform-browser';

import { Router } from '@angular/router';

@Component({
  selector: 'hdi-modal',
  standalone: true,
  templateUrl: './modal.component.html',
  styleUrl: './modal.component.scss',
  imports: [CommonModule, IconComponent],
  host: {
    id: crypto.randomUUID().toString(),
  },
})
export class ModalComponent
  implements AfterViewChecked, AfterContentChecked, OnDestroy
{
  @Input() disabled: boolean = false;
  @Input() showModal: boolean = false;
  @Input() videoSrc: SafeResourceUrl = {} as SafeResourceUrl;
  @Input() videoId: string = '';
  @Input() thumbnailSrc: string = '';
  @Input() type: ModalType = ModalType.DEFAULT;
  IconShare = 'link';
  IconClose = 'close';
  @Input() size: Sizes = Sizes.LARGE;
  @Input() color: Colors = Colors.WHITE;

  @Output() closeModalTrigger = new EventEmitter<boolean>();

  @ViewChild('modalContent') modalContent: ElementRef = {} as ElementRef;
  @ViewChild('modalContentBody') modalContentBody: ElementRef =
    {} as ElementRef;
  @ViewChild('modalContentVideo') modalContentVideo: ElementRef =
    {} as ElementRef;
  public clipboardLabel: boolean = false;
  private parentElement: ElementRef<HTMLElement> = {} as ElementRef;
  private routerLinkEventListener?: EventListener;

  constructor(
    private readonly renderer: Renderer2,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly router: Router,
    private readonly el: ElementRef
  ) {}

  ngAfterContentChecked(): void {
    this.parentElement = this.el.nativeElement.parentElement;
    this.renderer.setStyle(this.parentElement, 'position', 'relative');

    const routerLinkElement =
      this.modalContentBody?.nativeElement?.querySelector('[routerLink]');
    if (routerLinkElement) {
      this.routerLinkEventListener = (event: Event) => {
        event.preventDefault();
        this.router.navigateByUrl(
          routerLinkElement.getAttribute('routerLink') || ''
        );
      };
      routerLinkElement.addEventListener('click', this.routerLinkEventListener);
    }
  }

  ngAfterViewChecked(): void {
    if (this.modalContentBody?.nativeElement.children[0]) {
      this.renderer.addClass(
        this.modalContentBody.nativeElement.children[0],
        'w-100'
      );
    }
  }

  openModal(): void {
    if (this.disabled || !this.showModal) {
      return;
    }
    if (this.document?.body) {
      this.renderer.addClass(this.document.body, 'no-scroll');
    }
  }

  closeModal(): void {
    this.renderer.addClass(this.modalContent.nativeElement, 'slide-up');
    setTimeout(() => {
      this.renderer.removeClass(this.document.body, 'no-scroll');
      this.showModal = false;
      this.closeModalTrigger.emit(false);
    }, 350);
  }

  closeModalVideo(event: Event): void {
    event.preventDefault();
    this.renderer.addClass(this.modalContentVideo.nativeElement, 'slide-down');
    setTimeout(() => {
      this.renderer.removeClass(this.document.body, 'no-scroll');
      this.showModal = false;
      this.closeModalTrigger.emit(false);
    }, 350);
  }

  shareVideo(event: Event, videoId: string): void {
    event.preventDefault();
    event.stopPropagation();
    if (typeof window !== 'undefined') {
      const currentUrl = `${window.location.origin}${window.location.pathname}`;
      navigator.clipboard
        .writeText(`${currentUrl}?videoId=${videoId}`)
        .then(() => {
          this.clipboardLabel = true;
          setTimeout(() => {
            this.clipboardLabel = false;
          }, 500);
        })
        .catch((err) => {
          console.error('Could not copy text: ', err);
        });
    }
  }

  ngOnDestroy(): void {
    if (this.routerLinkEventListener) {
      this.modalContentBody.nativeElement.removeEventListener(
        'click',
        this.routerLinkEventListener
      );
    }
  }
}
